import {
  Component,
  DestroyRef,
  Inject,
  OnInit,
  inject,
  signal,
} from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  FormGroupDirective,
  NgForm,
  Validators,
  ReactiveFormsModule,
} from '@angular/forms';
import {
  MAT_DIALOG_DATA,
  MatDialogRef,
  MatDialogContent,
  MatDialogActions,
  MatDialogClose,
} from '@angular/material/dialog';
import { ErrorStateMatcher } from '@angular/material/core';
import { Building, Floor } from '../../../models';
import { UtilsService } from '@core/services/utils.service';
import { FloorService } from '../../../services';
import { ToastAlertService } from '@core/services';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ButtonComponent } from '@shared/components/button/button.component';
import { TextAreaComponent } from '@shared/components/text-area/text-area.component';
import { NgIf } from '@angular/common';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatButtonModule } from '@angular/material/button';
import { TitleComponent } from '@shared/components/title/title.component';
import { MatCardModule } from '@angular/material/card';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { InputComponent } from '@shared/components';
import {
  MatAccordion,
  MatExpansionPanel,
  MatExpansionPanelHeader,
  MatExpansionPanelTitle,
} from '@angular/material/expansion';

export class FloorErrorMatcher implements ErrorStateMatcher {
  isErrorState(
    control: FormGroup | null,
    form: FormGroupDirective | NgForm | null
  ): boolean {
    const isSubmitted = form && form.submitted;
    return !!(
      control &&
      control.invalid &&
      (control.dirty || control.touched || isSubmitted)
    );
  }
}

@Component({
  selector: 'app-floor-create-modal',
  templateUrl: './floor-create-modal.component.html',
  standalone: true,
  imports: [
    MatCardModule,
    TitleComponent,
    MatButtonModule,
    MatTooltipModule,
    MatIconModule,
    ReactiveFormsModule,
    MatDialogContent,
    MatFormFieldModule,
    MatInputModule,
    NgIf,
    TextAreaComponent,
    MatDialogActions,
    ButtonComponent,
    MatDialogClose,
    MatCheckboxModule,
    InputComponent,
    MatAccordion,
    MatExpansionPanel,
    MatExpansionPanelHeader,
    MatExpansionPanelTitle,
  ],
})
export class FloorCreateModalComponent implements OnInit {
  formBuilder = inject(FormBuilder);
  public operation = '';
  public floorForm!: FormGroup;
  public currentBuilding: Building;
  matcher = new FloorErrorMatcher();
  private _utils = inject(UtilsService);
  public planoBase64?: string;
  public _floor = inject(FloorService);

  private destroyRef = inject(DestroyRef);
  private alertService = inject(ToastAlertService);

  readonly panelOpenState = signal(true);
  messageHelpPassword = signal<string>(
    `El tamaño máximo permitido para subir una imagen es de 5 MB. Resolución recomendada del mapa 1920*1080 píxeles.`
  );

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    public dialogRef: MatDialogRef<FloorCreateModalComponent>
  ) {
    this.currentBuilding = this.data.currentBuilding;
    this.dialogRef.disableClose = true;
  }

  async ngOnInit() {
    console.log(this.data);
    if (this.data.action?.map) {
      this.planoBase64 = this.data.action?.map;
    } else {
      this.planoBase64 = '../../../../../../assets/img/placeholder.svg';
    }

    if (typeof this.data.action != 'undefined' && this.data.action?.floorid) {
      this.operation = 'Modificar';
      this.floorForm = this.formBuilder.group({
        floorId: [this.data.action?.floorid],
        name: [
          this.data.action?.name,
          [Validators.required, Validators.maxLength(50)],
        ],
        description: [this.data.action?.description, Validators.maxLength(256)],
        isParking: [this.data.action?.isparking === '1' ? 1 : 0],
        buildingid: [this.currentBuilding?.buildingid, Validators.required],
        imagemap: [this.data.action?.map],
      });
    } else {
      this.operation = 'Crear';
      this.floorForm = this.formBuilder.group({
        name: ['', [Validators.required, Validators.maxLength(50)]],
        description: ['', Validators.maxLength(256)],
        isParking: [false, Validators.required],
        buildingid: [this.currentBuilding?.buildingid, Validators.required],
        imagemap: ['', Validators.required],
      });
    }
  }

  get id_floor() {
    return this.floorForm.get('id');
  }

  clearForm() {
    this.ngOnInit();
  }

  async loadBase64Image(evt: any) {
    const file = evt.target.files[0];

    // Verificar si el archivo es una imagen y su tamaño
    const maxSizeInBytes = 5 * 1024 * 1024; // 5MB en bytes
    if (file && file.type.startsWith('image/') && file.size <= maxSizeInBytes) {
      await this._utils
        .convertFileToBase64(file)
        .subscribe((converted: string) => {
          this.planoBase64 = 'data:image/png;base64,' + converted;
          this.floorForm.get('imagemap')?.patchValue(this.planoBase64);
        });
    } else {
      // Mostrar mensaje de error o manejar el error de alguna forma
      alert('Por favor, selecciona una imagen de menos de 5MB.');
    }
  }

  onSubmit() {
    if (this.floorForm.valid) {
      if (typeof this.data.action != 'undefined' && this.data.action?.floorid) {
        const floor: Floor = <Floor>this.floorForm.value;

        this._floor
          .updateFloor(floor, this.data.action.floorid)
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe((response: any) => {
            if (response.code === 10000) {
              this.dialogRef.close(true);
              this.alertService.sucess(
                'Nivel ' + floor.name + ' modificado exitosamente.'
              );
            } else {
              this.alertService.error(response.detail);
            }
          });
      } else {
        const floor: Floor = <Floor>this.floorForm.value;

        this._floor
          .storeFloor(floor)
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe((response: any) => {
            if (response.code === 10000) {
              this.dialogRef.close(true);
              this.alertService.sucess('Nivel ' + floor.name + ' creado');
            } else {
              this.alertService.error(response.detail);
            }
          });
      }
    }
  }
}
